import axios from "axios";
import * as Sentry from "@sentry/react";
import { getRestApiUrl } from "./backendOverride";

// ---------------------------------------------------------------------------
// Incidencias de proveedores de mensajería (360dialog / Meta).
//
// El backend expone la lista de incidencias ACTIVAS para que un operador pueda
// encender un aviso en el dashboard sin desplegar. Contrato (IV-007):
//
//   GET /config/incidents → { incidents: [{ id, provider, severity, message,
//                                           startedAt, active }] }
//
// Sin incidencias activas devuelve `{ incidents: [] }` (200, no 404).
//
// POR QUÉ UN CLIENTE PROPIO Y NO EL `API` COMPARTIDO (revisión de dashboard#192):
// el interceptor de respuesta de `src/axios.js` hace logout incondicional ante
// un `UNAUTHORIZED` — `localStorage.clear()` + `window.location.replace("/")`.
// Ese interceptor corre ANTES del catch de aquí, así que un 401 en esta llamada
// echaría al usuario de la aplicación aunque este módulo devolviese `[]`. Un
// aviso informativo no puede tener poder para cerrar sesiones: este cliente
// manda el token pero NO instala interceptor de respuesta.
//
// Los headers `sentry-trace`/`baggage` los sigue inyectando Sentry por
// `tracePropagationTargets` (observability/init.js), que es global al navegador
// y no depende de la instancia de axios.
//
// REGLA DURA: este módulo lo consume un banner montado en DashboardLayout, que
// envuelve TODA la app autenticada. Nunca puede lanzar. Ante cualquier fallo
// —endpoint aún no desplegado, 500, timeout, payload con otra forma— devuelve
// lista vacía y el dashboard sigue como si nada.
// ---------------------------------------------------------------------------

export const INCIDENTS_ENDPOINT = "/config/incidents";

/** Canal público por el que el backend anuncia los cambios de aviso. */
export const INCIDENTS_CHANNEL = "platform_status";
export const INCIDENTS_EVENT = "provider-incident";

// Sin token de `info` en DesignTokens: cae al branch por defecto del styled
// (fondo blanco, texto gris) sin necesidad de añadir color nuevo.
const SEVERITIES = ["error", "warning", "info"];
const SEVERITY_ORDER = { error: 0, warning: 1, info: 2 };
const PROVIDERS = ["360", "META"];

const incidentsApi = axios.create();

incidentsApi.interceptors.request.use((config) => {
  config.headers.Authorization = `Bearer ${localStorage.getItem("token")}`;
  config.baseURL = `${getRestApiUrl()}`;
  return config;
});
// A propósito: NINGÚN interceptor de respuesta. Ver la nota de arriba.

// Reportar cada fallo inundaría Sentry, pero una bandera de módulo apagaría la
// señal para el resto de la sesión: si el endpoint falla al cargar y se cae de
// verdad tres horas más tarde, nadie se enteraría. Ventana temporal en su lugar.
const FAILURE_REPORT_WINDOW_MS = 30 * 60 * 1000;
let lastFailureReportedAt = 0;

const reportFailure = (error) => {
  const now = Date.now();
  if (now - lastFailureReportedAt < FAILURE_REPORT_WINDOW_MS) return;
  lastFailureReportedAt = now;
  // `warning`, no `exception`: mientras el endpoint no esté desplegado el
  // 404 es el estado ESPERADO, y un error aquí no degrada al usuario.
  Sentry.captureMessage("providerIncidents.fetch.failed", {
    level: "warning",
    tags: { component: "IncidentBanner", op: "fetch" },
    extra: { httpCode: error?.response?.status },
  });
};

const isRenderable = (incident) =>
  incident
  && typeof incident.message === "string"
  && incident.message.trim() !== ""
  && incident.active !== false
  && PROVIDERS.includes(incident.provider);

const normalize = (incident) => ({
  id: incident.id || `${incident.provider}-${incident.startedAt || ""}`,
  provider: incident.provider,
  // Una severidad desconocida no debe dejar la barra sin estilo: cae a "warning".
  severity: SEVERITIES.includes(incident.severity) ? incident.severity : "warning",
  message: incident.message.trim(),
  startedAt: incident.startedAt || "",
});

const bySeverity = (a, b) => SEVERITY_ORDER[a.severity] - SEVERITY_ORDER[b.severity];

export const sortIncidents = (incidents) => [...incidents].sort(bySeverity);

/**
 * Aplica a la lista en memoria el aviso que llegó por Pusher: lo añade, lo
 * reemplaza si ya estaba, o lo quita si vino desactivado.
 */
export const applyIncidentEvent = (incidents, rawIncident) => {
  const id = rawIncident?.id;
  if (!id) return incidents;

  const rest = incidents.filter((incident) => incident.id !== id);
  if (!isRenderable(rawIncident)) return sortIncidents(rest);

  return sortIncidents([...rest, normalize(rawIncident)]);
};

/**
 * Devuelve las incidencias activas, ya normalizadas y ordenadas por severidad
 * (primero las más graves). Nunca lanza: ante cualquier fallo devuelve [].
 */
export const getProviderIncidents = async () => {
  try {
    const { data } = await incidentsApi.get(INCIDENTS_ENDPOINT);
    const incidents = data?.incidents;
    if (!Array.isArray(incidents)) return [];
    return sortIncidents(incidents.filter(isRenderable).map(normalize));
  } catch (error) {
    reportFailure(error);
    return [];
  }
};

export default getProviderIncidents;
